import { useEffect, useMemo, useState } from "react";
import { Text } from "./typography";
import { api, apiBase } from "../../modules/api_client";

interface ImageProps {
  /**
   * Image source — accepts a URL, data URI, or raw base64 string.
   * For raw base64, the image type is auto-detected from the magic bytes.
   * Also accepts an object with a `value` property containing any of the above.
   */
  image: string | object | null | undefined;
  /**
   * Alt text for the image
   */
  alt?: string;
  /**
   * Image type (default: "svg+xml")
   */
  imageType?: string;
  /**
   * Width and height classes (default: "w-10 h-10")
   */
  size?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Placeholder text when image is not available (default: "—")
   */
  placeholder?: string;
  /**
   * Show placeholder background (default: true)
   */
  showPlaceholder?: boolean;
  /**
   * Object-fit mode for the image (default: "contain")
   */
  objectFit?: "contain" | "cover";
}

/**
 * Detect image type from base64 string
 * PNG: iVBORw0KGgo
 * JPEG: /9j/4AAQ
 * GIF: R0lGODlh
 * WebP: UklGR
 * SVG: PHN2Zw (but we'll use svg+xml as default)
 */
function detectImageType(base64String: string): string | null {
  if (!base64String || base64String.length < 4) return null;

  const prefix = base64String.substring(0, 12);

  if (prefix.startsWith("iVBORw0KGgo")) return "png";
  if (prefix.startsWith("/9j/")) return "jpeg";
  if (prefix.startsWith("R0lGODlh")) return "gif";
  if (prefix.startsWith("UklGR")) return "webp";
  if (prefix.startsWith("PHN2Zw") || prefix.includes("<svg")) return "svg+xml";

  return null;
}

export function resolveImageSource(imageSrc: string | null | undefined): string | null {
  if (!imageSrc) return null;
  if (/^\/api\/v\d+\//i.test(imageSrc) && apiBase) {
    return `${apiBase}${imageSrc}`;
  }
  return imageSrc;
}

/**
 * Reusable image primitive. Accepts a URL, data URI, or raw base64 string
 * and renders the appropriate <img>. Falls back to a placeholder on missing
 * input or load error.
 */
export function Image({
  image,
  alt = "Image",
  imageType,
  size = "w-10 h-10",
  className = "",
  placeholder = "—",
  showPlaceholder = true,
  objectFit = "contain",
}: ImageProps) {
  const [hasError, setHasError] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  const imageSrc = useMemo(() => {
    if (!image || hasError) return null;

    let base64String: string | null = null;

    if (typeof image === "string") {
      base64String = image;
    } else if (
      typeof image === "object" &&
      image !== null &&
      "value" in image
    ) {
      // Image might be an object with a value property
      const imageValue = (image as { value?: string }).value;
      if (imageValue && typeof imageValue === "string") {
        base64String = imageValue;
      }
    }

    if (!base64String) return null;

      // Detect base64 image first — JPEG starts with "/9j/" which would
      // otherwise be misread as a relative URL by the URL check below.
      const detectedType = detectImageType(base64String);
      if (detectedType) {
        return `data:image/${imageType || detectedType};base64,${base64String}`;
      } else if (/^(https?:|data:|blob:|\/)/i.test(base64String)) {
        return resolveImageSource(base64String);
      } else {
        // Unknown — assume base64 of the requested or default type.
        return `data:image/${imageType || "png"};base64,${base64String}`;
      }
  }, [hasError, image, imageType]);

  const shouldFetchBlob = Boolean(
    imageSrc &&
      /^(\/api\/v\d+\/|https?:\/\/[^ ]+\/api\/v\d+\/)/i.test(imageSrc) &&
      !imageSrc.startsWith("blob:")
  );

  useEffect(() => {
    if (!imageSrc || hasError || !shouldFetchBlob) {
      setBlobUrl(null);
      return;
    }

    let active = true;
    let objectUrl: string | null = null;

    setBlobUrl(null);

    api
      .get(imageSrc, { responseType: "blob" })
      .then((response) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(response.data);
        setBlobUrl(objectUrl);
      })
      .catch(() => {
        if (!active) return;
        setHasError(true);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [hasError, imageSrc, shouldFetchBlob]);

  const finalSrc = blobUrl ?? imageSrc;

  // Show placeholder if no image or error occurred
  if (!finalSrc || hasError) {
    if (!showPlaceholder) {
      return null;
    }
    return (
      <div
        className={`flex items-center justify-center ${size} bg-uiSecondary rounded ${className}`}
      >
        <Text variant="bodyXs" tone="body">
          {placeholder}
        </Text>
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-center ${size} ${className}`}>
      <img
        src={finalSrc}
        alt={alt}
        className={`${size} ${objectFit === "cover" ? "object-cover" : "object-contain"} rounded`}
        onError={() => setHasError(true)}
      />
    </div>
  );
}
